import type { ModelStackId } from './modelStacks'
import type { OptimizationBlockState, SkillPreMatch } from './types'

export interface OptimizationQualityGates {
  all_blocks_done: boolean
  zero_failed: boolean
  summary_block_done: boolean
  work_experience_blocks_done: boolean
  summary_has_text?: boolean
  contact_has_name?: boolean
  target_title_set?: boolean
  summary_programmatic_pass?: boolean
  education_programmatic_pass?: boolean
}

export interface OptimizationQualityEval {
  evaluated_at: string
  model_stack: ModelStackId
  blocks_total: number
  blocks_done: number
  blocks_failed: number
  blocks_running_or_pending: number
  primary_pass: boolean
  gates: OptimizationQualityGates
  programmatic_failures?: {
    summary: string[]
    education: string[]
  }
  gate_results?: Array<{
    gate_id?: string
    pass?: boolean
    status?: string
    detail?: string
    reason?: string
    message?: string
    evidence?: { report_only?: boolean }
  }>
  cost_levers?: {
    skill_pre_match?: SkillPreMatch
  }
  skill_gap?: SkillPreMatch & {
    green_count?: number
    recommendations?: string[]
  }
  cost_usd?: number
  cost_rate_source?: string
  token_usage?: {
    input_tokens?: number
    output_tokens?: number
    cache_creation_input_tokens?: number
    cache_read_input_tokens?: number
    billable_input_tokens?: number
    llm_calls?: number
    by_model?: Record<
      string,
      {
        input_tokens?: number
        output_tokens?: number
        cache_creation_input_tokens?: number
        cache_read_input_tokens?: number
        billable_input_tokens?: number
        calls?: number
      }
    >
    calls?: Array<{
      model_id?: string
      call_type?: string | null
      usage?: {
        input_tokens?: number
        output_tokens?: number
        cache_creation_input_tokens?: number
        cache_read_input_tokens?: number
      }
    }>
  }
}

export function computeOptimizationQualityEval(
  blocks: OptimizationBlockState[],
  modelStack: ModelStackId
): OptimizationQualityEval {
  const blocks_total = blocks.length
  const blocks_done = blocks.filter((b) => b.status === 'done' || b.status === 'skipped').length
  const blocks_failed = blocks.filter((b) => b.status === 'failed').length
  const blocks_running_or_pending = blocks.filter(
    (b) => b.status === 'pending' || b.status === 'running'
  ).length

  const summary = blocks.find((b) => b.section_type === 'professional_summary')
  const wxBlocks = blocks.filter((b) => b.section_type === 'work_experience')

  const gates: OptimizationQualityGates = {
    all_blocks_done: blocks_total > 0 && blocks_done === blocks_total,
    zero_failed: blocks_failed === 0,
    summary_block_done: summary?.status === 'done' || summary?.status === 'skipped',
    work_experience_blocks_done:
      wxBlocks.length === 0 ||
      wxBlocks.every((b) => b.status === 'done' || b.status === 'skipped'),
    summary_programmatic_pass: true,
    education_programmatic_pass: true,
  }

  const primary_pass =
    gates.all_blocks_done &&
    gates.zero_failed &&
    gates.summary_block_done &&
    gates.work_experience_blocks_done

  return {
    evaluated_at: new Date().toISOString(),
    model_stack: modelStack,
    blocks_total,
    blocks_done,
    blocks_failed,
    blocks_running_or_pending,
    primary_pass,
    gates,
  }
}

/** API may persist a partial quality_eval (e.g. cost-only on worker failure) without gates. */
export function mergeOptimizationQualityEval(
  blocks: OptimizationBlockState[],
  modelStack: ModelStackId,
  apiEval?: OptimizationQualityEval | null
): OptimizationQualityEval | null {
  if (!blocks.length) return null
  const computed = computeOptimizationQualityEval(blocks, modelStack)
  if (!apiEval) return computed

  const gates =
    apiEval.gates && typeof apiEval.gates === 'object'
      ? { ...computed.gates, ...apiEval.gates }
      : computed.gates

  return {
    ...computed,
    ...apiEval,
    model_stack: apiEval.model_stack ?? modelStack,
    blocks_total: apiEval.blocks_total ?? computed.blocks_total,
    blocks_done: apiEval.blocks_done ?? computed.blocks_done,
    blocks_failed: apiEval.blocks_failed ?? computed.blocks_failed,
    blocks_running_or_pending:
      apiEval.blocks_running_or_pending ?? computed.blocks_running_or_pending,
    primary_pass:
      typeof apiEval.primary_pass === 'boolean' ? apiEval.primary_pass : computed.primary_pass,
    gates,
  }
}
